import React, { useEffect, useRef, useState } from "react";
import styled from 'styled-components';

import Button from "components/Button";
import TextArea from "antd/lib/input/TextArea";
import Icon from "components/Icon";
import { Text, Title } from "components/Text";

import useChat from "./hooks/useChat";

import ChatbotMessage, { ChatMessageLoading } from "./ChatMessage";

import { CHATBOT_NAME, CHATBOT_START_MESSAGE, PREDEFINED_QUESTIONS } from './constants';

const Chatbot = ({
  placeholder = "Ask a question"
}) => {
  const messagesRef = useRef();
  const chatState = useChat();
  const [question, setQuestion] = useState('');

  useEffect(() => {
    if (!chatState.historyLoaded) {
      chatState.loadHistory();
    }
  }, [chatState]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [chatState.messages]);

  const handleSend = () => {
    if (question.trim().length && !chatState.waitingForResponse) {
      chatState.askQuestion(question);
      setQuestion('');
    }
  };

  const handleSendPredefinedQuestion = (question) => {
    if (question.trim()) {
      chatState.askQuestion(question);
    }
  }

  return (
    <ChatWrapper>
      <MessagesWrapper ref={messagesRef}>
        {chatState.messages.length ? (
          <>
            {chatState.messages.map((message, index) => (
              <ChatbotMessage key={index} {...message} />
            ))}
            {chatState.waitingForResponse ? (
              <ChatMessageLoading key="loading" />
            ) : null}
          </>
        ) : (
          <ChatBanner>
            <Icon name="lightSparkle" size={40} color="#A4B0BA" strokeWidth={1} className="chatbotIcon" />
            <Title level={3} className="chatbotName">{CHATBOT_NAME}</Title>
            {CHATBOT_START_MESSAGE && <Text className="chatbotMessage">{CHATBOT_START_MESSAGE}</Text>}
          </ChatBanner>
        )}
      </MessagesWrapper>
      <PredefinedQuestions>
        <Text className="questionsTitle">
          <Icon name="lightSparkle" size={16} color="#828C94" strokeWidth={1} className="chatbotIcon" />
          {' '} Suggested questions
        </Text>
        {PREDEFINED_QUESTIONS.map((question, index) => (
          <PredifinedQuestion
            key={index}
            onClick={() => {
              handleSendPredefinedQuestion(question);
            }}
          >
            {question}
          </PredifinedQuestion>
        ))}
      </PredefinedQuestions>
      <MessageBox>
        <TextArea rows={3}
          name="question"
          placeholder={placeholder}
          autoComplete="off"
          autoFocus
          maxLength={2000}
          style={{
            resize: 'none'
          }}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if ((e.code === '13' || e.keyCode === 13) && e.shiftKey === false) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          size="large"
          shape="circle"
          type="primary"
          htmlType="submit"
          icon={{ name: "sendAndShare", size: 20, style: { marginTop: '4px', marginLeft: '4px' } }}
          onClick={() => handleSend()}
          disabled={question.trim().length === 0 || chatState.waitingForResponse}
        />
      </MessageBox>
    </ChatWrapper>
  );
}

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MessagesWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 8px;
  overflow-y: auto;
  padding: 16px 0;
`;

const MessageBox = styled.div`
  padding: 16px;
  display: flex;
  gap: 12px;
`;

const ChatBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
  margin-bottom: auto;

  .chatbotName, .chatbotMessage {
    color: #A4B0BA;
  }

  .chatbotName {
    margin-bottom: 0;
  }

  .chatbotIcon {
    margin-bottom: 4px;
  }
`;

const PredefinedQuestions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 16px;
  gap: 8px;
  margin-top: auto;

  .questionsTitle {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #828C94;
  }
`;

const PredifinedQuestion = styled.div`
  border-radius: 4px;
  border: 1px solid #C7F4FC;
  background: #F5FDFF;
  color: #2E8BB0;
  padding: 8px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  cursor: pointer;
`;

export default Chatbot;